import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { KpiCard } from '../components/ui/Card';
import { Card } from '../components/ui/Card';
import {
  Users,
  Target,
  TrendingUp,
  DollarSign,
  Bot,
  Activity,
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../lib/utils';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';
import { useAuth } from '../store/auth';
import { usePublisherLookup } from '../hooks/useLookups';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { SubIdDetailModal } from '../components/SubIdDetailModal';

const ranges = ['today', 'yesterday', '7d', '30d', 'custom'] as const;
type Range = (typeof ranges)[number];

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

export function OverviewsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const publisherLookup = usePublisherLookup();
  // 1 for admin, 2 for publisher — slows publisher polling to half rate.
  const pollMult = usePollMultiplier();
  // NOTE: previously auto-scoped to mine=true for user role (batch 2).
  // Reverted on user request — Overviews stays a network-wide view for
  // both roles. The /earnings page is the dedicated "personal data" view.
  const [range, setRange] = useState<Range>('today');
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const [drilldownTarget, setDrilldownTarget] = useState<
    { publisherId: number | null; subIdHint?: string } | undefined
  >(undefined);
  const customValid = customFrom <= customTo;
  const statsParams =
    range === 'custom' ? { range, from: customFrom, to: customTo } : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  const overview = useQuery({
    queryKey: ['stats-overview', rangeKey],
    queryFn: () =>
      apiClient.get<{
        clicksTotal: number;
        clicksUnique: number;
        clicksBot: number;
        clicksQuality: number;
        conversionsApproved: number;
        conversionsPending: number;
        revenueGross: number;
        payoutGross: number;
        profit: number;
        cr: number;
        epc: number;
        range: string;
      }>(`/api/v1/stats/overview`, statsParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // D16: typed instead of any[] — matches the shape returned by /api/v1/stats/by-publisher
  // subIdHint: present for clicks-only sub_ids that aren't registered as a publisher
  // (someone hit a tracking link with an arbitrary ?sub_id=... value).
  interface ByPublisherRow {
    publisherId: number | null;
    clicksQuality: number;
    conversionsApproved: number;
    revenueGross: number;
    subIdHint?: string;
  }
  // D16: typed instead of any[]
  interface ByCountryRow {
    country: string | null;
    clicksTotal: number;
    clicksQuality: number;
    conversionsApproved: number;
    revenueGross: number;
  }

  // BUGFIX: previously these had no refetchInterval — only the KPI cards
  // auto-updated. Top Publishers + Top Countries tables stayed frozen until
  // user changed range or hard-refreshed, making the dashboard feel stale
  // even though clicks were arriving in AE. Match the KPI polling cadence.
  const byPublisher = useQuery({
    queryKey: ['stats-by-publisher', rangeKey],
    queryFn: () =>
      apiClient.get<ByPublisherRow[]>(
        `/api/v1/stats/by-publisher`,
        statsParams,
      ),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  const byCountry = useQuery({
    queryKey: ['stats-by-country', rangeKey],
    queryFn: () =>
      apiClient.get<ByCountryRow[]>(`/api/v1/stats/by-country`, statsParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  const data = overview.data ?? {
    clicksTotal: 0,
    clicksUnique: 0,
    clicksBot: 0,
    clicksQuality: 0,
    conversionsApproved: 0,
    conversionsPending: 0,
    revenueGross: 0,
    payoutGross: 0,
    profit: 0,
    cr: 0,
    epc: 0,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">
            {isAdmin ? 'Overviews' : 'My Dashboard'}
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            //{' '}
            {isAdmin
              ? 'all publishers'
              : user?.publisherId
                ? `publisher: ${publisherLookup.getName(user.publisherId)}`
                : 'publisher: unlinked'}
            {' · '}
            {range === 'custom' ? `${customFrom}..${customTo} UTC` : range}
          </p>
          {/* Inform users that today's clicks have inherent ~30-60s ingestion
              delay from Cloudflare Analytics Engine. Without this hint, users
              expect realtime numbers and panic when their fresh clicks aren't
              reflected immediately. /clicks page uses Durable Object stream
              (sub-2s) — point them there for live monitoring. */}
          {range === 'today' && (
            <p className="text-[10px] text-text-tertiary font-mono mt-1">
              <span className="text-neon-yellow">ⓘ</span> Today's clicks have
              ~30-60s AE ingestion lag. For sub-2s live stream, see{' '}
              <a href="/clicks" className="text-neon-cyan hover:underline">
                /clicks
              </a>
              .
            </p>
          )}
        </div>
        <div className="flex flex-col sm:items-end gap-2">
          <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
            {ranges.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all ${
                  range === r
                    ? 'bg-neon-cyan/10 text-neon-cyan'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              <span className="text-text-tertiary">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              {!customValid && (
                <span className="text-neon-red">from must be before to</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Clicks"
          value={formatNumber(data.clicksTotal)}
          icon={<Users size={16} />}
          accent="cyan"
        />
        <KpiCard
          label="Conversions"
          value={formatNumber(data.conversionsApproved)}
          icon={<Target size={16} />}
          accent="magenta"
        />
        <KpiCard
          label="Conversion Rate"
          value={formatPercent(data.cr, 2)}
          icon={<TrendingUp size={16} />}
          accent="green"
        />
        <KpiCard
          label="Revenue"
          value={formatCurrency(data.revenueGross)}
          icon={<DollarSign size={16} />}
          accent="yellow"
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard
          label="Quality Clicks"
          value={formatNumber(data.clicksQuality)}
          accent="cyan"
        />
        <KpiCard
          label="Bot Clicks"
          value={formatNumber(data.clicksBot)}
          icon={<Bot size={14} />}
          accent="orange"
        />
        <KpiCard label="EPC" value={formatCurrency(data.epc)} accent="green" />
      </div>

      {/* Top tables — both roles see network-wide breakdowns. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-0">
          <div className="px-5 py-4 border-b border-border-subtle">
            <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
              <Users size={14} /> Top Publishers
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-cyber">
              <thead>
                <tr>
                  <th>Publisher</th>
                  <th className="text-right">Clicks</th>
                  <th className="text-right">Conv</th>
                  <th className="text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(byPublisher.data ?? []).slice(0, 8).map((row, i) => (
                  <tr
                    key={i}
                    onClick={() =>
                      setDrilldownTarget({
                        publisherId: row.publisherId ?? null,
                        subIdHint: row.subIdHint,
                      })
                    }
                    className="cursor-pointer hover:bg-neon-magenta/5"
                    title="Click for detailed breakdown"
                  >
                    <td
                      className="font-mono text-xs text-neon-cyan"
                      title={
                        row.publisherId
                          ? `ID: ${row.publisherId}`
                          : row.subIdHint
                            ? `Unregistered sub_id: ${row.subIdHint}`
                            : undefined
                      }
                    >
                      {row.publisherId != null ? (
                        <SubIdLightning
                          subId={publisherLookup.getSubId(row.publisherId)}
                        >
                          {publisherLookup.getName(row.publisherId)}
                        </SubIdLightning>
                      ) : row.subIdHint ? (
                        <span className="text-text-primary">
                          {row.subIdHint}
                        </span>
                      ) : (
                        <span className="text-text-tertiary italic">
                          unlinked
                        </span>
                      )}
                    </td>
                    <td className="text-right numeric">
                      {formatNumber(row.clicksQuality)}
                    </td>
                    <td className="text-right numeric text-neon-magenta">
                      {formatNumber(row.conversionsApproved)}
                    </td>
                    <td className="text-right numeric text-neon-yellow">
                      {formatCurrency(row.revenueGross)}
                    </td>
                  </tr>
                ))}
                {(byPublisher.data ?? []).length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-text-tertiary py-8 font-mono text-xs"
                    >
                      no data yet — waiting for traffic...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-0">
          <div className="px-5 py-4 border-b border-border-subtle">
            <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
              <Target size={14} /> Top Countries
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-cyber">
              <thead>
                <tr>
                  <th>Country</th>
                  <th className="text-right">Clicks</th>
                  <th className="text-right">Conv</th>
                  <th className="text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(byCountry.data ?? []).slice(0, 8).map((row, i) => (
                  <tr key={i}>
                    <td>
                      <CountryFlag iso2={row.country} showCode size={20} />
                    </td>
                    <td className="text-right numeric">
                      {formatNumber(row.clicksTotal)}
                    </td>
                    <td className="text-right numeric text-neon-magenta">
                      {formatNumber(row.conversionsApproved)}
                    </td>
                    <td className="text-right numeric text-neon-yellow">
                      {formatCurrency(row.revenueGross)}
                    </td>
                  </tr>
                ))}
                {(byCountry.data ?? []).length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-text-tertiary py-8 font-mono text-xs"
                    >
                      no data yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <SubIdDetailModal
        publisherId={drilldownTarget?.publisherId}
        subIdHint={drilldownTarget?.subIdHint}
        onClose={() => setDrilldownTarget(undefined)}
        range={range}
        customFrom={customFrom}
        customTo={customTo}
      />
    </div>
  );
}
